// CHANNEL SCALE · 채널 규모 — 좌: 합계 요약(총 구독자 + 규모 등급 배지 + 누적조회/총콘텐츠),
//                              우: 채널별 3종 테이블(구독자·콘텐츠·누적조회) + 합계.
//
// 실데이터 철칙:
//   · 누적 조회수는 YouTube만 신뢰 → IG/TikTok views=null 이면 "—". 합계도 YouTube 기준.
//   · 규모 등급(scaleLabel)이 null이면 배지 미표시(graceful).
//   · demographics(성별·연령·국가)는 소스 부재로 이 섹션에서 제외.
// 채널명은 3배 길이도 ellipsis로 잘림 처리, 바로가기 링크는 url 있을 때만.
//
// `갱신` 컬럼(축 A · 채널 지표 수집일)은 BE 필드가 도달했을 때만 만든다(R9). 합계행은 사이드바
// 배지와 같은 규칙으로 **가장 오래된 값**을 쓴다 — 두 값이 다르게 보이면 그 자체가 버그 신호다.
import { ExternalLink } from 'lucide-react'
import type { ProfileData } from '../../creatorsApi'
import { FRESHNESS_DOT, FRESHNESS_LABEL, formatMonthDay, freshnessState, oldestDate } from '../../freshness'
import { resolveMetric } from '../metric'
import { PLATFORM_LABEL, formatIsoDate } from '../overview/format'
import { SectionHeader } from './SectionHeader'
import { formatHandle, formatInt } from './format'

type ScaleChannel = ProfileData['channelScale']['channels'][number]

// 컬럼 폭 — 헤더/본문/합계가 같은 상수를 쓴다(따로 쓰면 어긋난다).
const W_SUB = 'basis-[96px]'
const W_CNT = 'basis-[64px]'
const W_VIEW = 'basis-[124px]'
const W_UPD = 'basis-[90px]'

export function ChannelScale({ scale }: { scale: ProfileData['channelScale'] }) {
  const { totalSubscribers, scaleLabel, channels } = scale
  if (channels.length === 0) return null

  // 필드가 아예 없는 것(구버전 BE)과 값이 null인 것("수집 대상 아님")은 다른 사실이다 → 키로 판정.
  const hasFreshness = channels.some((c) => 'lastCollectedAt' in c)
  const oldestCollectedAt = oldestDate(channels.map((c) => c.lastCollectedAt))

  const viewVals = channels
    .map((c) => c.views)
    .filter((v): v is number => v != null && Number.isFinite(v))
  const totalViews = viewVals.length > 0 ? viewVals.reduce((a, b) => a + b, 0) : null
  const totalContents = channels.reduce(
    (sum, c) => sum + (Number.isFinite(c.contents) ? c.contents : 0),
    0,
  )

  return (
    <section className="mt-[34px]">
      <SectionHeader label="CHANNEL SCALE · 채널 규모" />
      <div className="grid grid-cols-1 border border-line-hairline bg-paper md:[grid-template-columns:minmax(0,286px)_minmax(0,1fr)]">
        {/* LEFT · 합계 요약 */}
        <div className="border-b border-line-hairline p-[22px_24px] md:border-b-0 md:border-r">
          <div className="font-text text-[11px] tracking-[0.04em] text-fg-3">총 구독자</div>
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <div className="font-display text-[40px] font-bold leading-none tabular-nums text-ink-800">
              {formatInt(totalSubscribers)}
              <span className="text-brand-blue">.</span>
            </div>
            {scaleLabel && (
              <span className="inline-flex items-center whitespace-nowrap border border-brand-blue bg-blue-50 px-[9px] py-[3px] font-text text-[11px] font-semibold tracking-[0.02em] text-blue-600">
                {scaleLabel}
              </span>
            )}
          </div>

          <div className="mt-4 border-t border-line-hairline pt-4">
            <div className="flex items-baseline justify-between gap-3 py-[5px]">
              <span className="font-text text-[12px] text-fg-3">누적 조회수 · YouTube</span>
              <span className="font-display text-[16px] font-bold tabular-nums text-ink-800">
                {formatInt(totalViews)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3 py-[5px]">
              <span className="font-text text-[12px] text-fg-3">총 콘텐츠</span>
              <span className="font-display text-[16px] font-bold tabular-nums text-ink-800">
                {formatInt(totalContents)}
              </span>
            </div>
          </div>
          <div className="mt-3 font-text text-[12px] text-fg-3">{channels.length}개 채널 합산</div>
        </div>

        {/* RIGHT · 채널별 규모 3종 테이블 */}
        <div className="min-w-0 overflow-x-auto p-[20px_24px]">
          {/* 헤더·본문·합계를 감싸는 단일 래퍼가 최소 폭을 잡는다(행마다 따로 주면 어긋난다) */}
          <div className={hasFreshness ? 'min-w-[530px]' : 'min-w-[440px]'}>
            {/* header — 컬럼명 행: 시안대로 투명(Overview·Profile 표 헤더는 배경 없음) */}
            <div className="flex items-center gap-3 border-b border-line-hairline pb-2.5">
              <span className="min-w-0 flex-1 font-text text-[11px] tracking-[0.04em] text-fg-3">
                채널
              </span>
              <span className={`flex-none ${W_SUB} text-right font-text text-[11px] text-fg-3`}>
                구독자
              </span>
              <span className={`flex-none ${W_CNT} text-right font-text text-[11px] text-fg-3`}>
                콘텐츠
              </span>
              <span className={`flex-none ${W_VIEW} text-right font-text text-[11px] text-fg-3`}>
                누적 조회수
              </span>
              {hasFreshness && (
                <span className={`flex-none ${W_UPD} text-right font-text text-[11px] text-fg-3`}>
                  갱신
                </span>
              )}
            </div>
            {channels.map((ch, i) => (
              <ChannelRow
                key={`${ch.platform}-${ch.name ?? i}`}
                channel={ch}
                showUpdated={hasFreshness}
              />
            ))}
            {/* Σ 합계 */}
            <div className="flex items-center gap-3 border-t border-line-soft py-[11px]">
              <span className="min-w-0 flex-1 font-text text-[14px] font-semibold text-fg-1">
                합계
              </span>
              <span
                className={`flex-none ${W_SUB} text-right font-display text-[14px] font-bold tabular-nums text-ink-800`}
              >
                {formatInt(totalSubscribers)}
              </span>
              <span
                className={`flex-none ${W_CNT} text-right font-display text-[14px] font-bold tabular-nums text-ink-800`}
              >
                {formatInt(totalContents)}
              </span>
              <span
                className={`flex-none ${W_VIEW} text-right font-display text-[14px] font-bold tabular-nums text-ink-800`}
              >
                {formatInt(totalViews)}
              </span>
              {hasFreshness && (
                <span className={`flex-none ${W_UPD} text-right`}>
                  <UpdatedCell
                    collectedAt={oldestCollectedAt}
                    title="대상 채널 중 가장 오래된 수집일"
                    strong
                  />
                </span>
              )}
            </div>
            <div className="mt-3 break-keep font-text text-[11px] leading-[1.55] text-fg-3">
              Instagram·TikTok은 채널 누적 조회수를 제공하지 않아{' '}
              <span className="text-fg-2">—</span>로 표시됩니다. 합계는 YouTube 기준입니다.
              {hasFreshness && (
                <>
                  {' '}
                  갱신은 채널 지표(구독자·콘텐츠·누적조회) 수집일이며,{' '}
                  <span className="text-fg-2">—</span>는 자동 수집 대상이 아닌 채널입니다. 합계행은
                  가장 오래된 수집일을 표시합니다.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ChannelRow({ channel, showUpdated }: { channel: ScaleChannel; showUpdated: boolean }) {
  // IG/TikTok 은 @핸들, YouTube 는 채널명. social_name 없으면 플랫폼 라벨로 폴백.
  const name = formatHandle(channel.platform, channel.name) || PLATFORM_LABEL[channel.platform]
  // 누적조회 "—"의 사유는 셀 캡션이 아니라 블록 각주가 전달한다(표기 밀도 규칙).
  //   다만 정보를 버리진 않게 hover title 로는 남긴다.
  const views = resolveMetric({ value: channel.views, reason: channel.viewsUnavailableReason })
  return (
    <div className="flex items-center gap-3 border-b border-line-hairline py-[11px]">
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        {channel.url ? (
          <a
            href={channel.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 items-center gap-[5px] font-text text-[14px] text-fg-1 hover:text-brand-blue"
          >
            <span className="truncate">{name}</span>
            <ExternalLink className="h-3 w-3 flex-none text-brand-blue" aria-hidden />
          </a>
        ) : (
          <span className="truncate font-text text-[14px] text-fg-1">{name}</span>
        )}
        <span className="font-text text-[11px] text-fg-3">{PLATFORM_LABEL[channel.platform]}</span>
      </div>
      <span
        className={`flex-none ${W_SUB} text-right font-text text-[14px] font-medium tabular-nums text-ink-800`}
      >
        {formatInt(channel.subscribers)}
      </span>
      <span className={`flex-none ${W_CNT} text-right font-text text-[14px] tabular-nums text-fg-2`}>
        {formatInt(channel.contents)}
      </span>
      <span
        className={[
          `flex-none ${W_VIEW} text-right font-text text-[14px] tabular-nums`,
          channel.views == null ? 'text-fg-3' : 'font-medium text-ink-800',
        ].join(' ')}
        title={!views.hasValue && views.caption ? views.caption : undefined}
      >
        {formatInt(channel.views)}
      </span>
      {showUpdated && (
        <span className={`flex-none ${W_UPD} text-right`}>
          <UpdatedCell
            collectedAt={channel.lastCollectedAt}
            mediaCollectedAt={channel.lastMediaCollectedAt}
          />
        </span>
      )}
    </div>
  )
}

/** 갱신 셀 — 지표가 아니라 메타라 12px로 한 단 낮춘다. 색은 도트에만, 날짜는 좁은 폭에 맞춰 MM.DD. */
function UpdatedCell({
  collectedAt,
  mediaCollectedAt,
  title,
  strong,
}: {
  collectedAt: string | null | undefined
  mediaCollectedAt?: string | null
  title?: string
  strong?: boolean
}) {
  const state = freshnessState(collectedAt)
  const label = formatMonthDay(collectedAt)
  const full = formatIsoDate(collectedAt)
  const fullMedia = formatIsoDate(mediaCollectedAt)
  const hover =
    title ??
    (full
      ? `채널 지표 ${full}${fullMedia ? ` · 게시물 ${fullMedia}` : ''}`
      : '자동 수집 대상이 아닌 채널')
  return (
    <span className="inline-flex w-full items-center justify-end gap-1.5" title={hover}>
      <span
        className={`h-1.5 w-1.5 flex-none rounded-r-pill ${FRESHNESS_DOT[state]}`}
        role="img"
        aria-label={FRESHNESS_LABEL[state]}
      />
      <span
        className={[
          'font-text text-[12px] tabular-nums',
          label ? (strong ? 'font-semibold text-fg-2' : 'text-fg-2') : 'text-fg-disabled',
        ].join(' ')}
      >
        {label ?? '—'}
      </span>
    </span>
  )
}
